import React, { useCallback, useEffect, useState } from "react";
import Customers from "../components/cards/Customers";
import DuePayable from "../components/cards/DuePayable";
import AvailableStock from "../components/cards/AvailableStock";
import Revenue from "../components/cards/Revenue";
import YearlySales from "../components/cards/YearlySales";
import Orders from "../components/cards/Orders";
import Visitors from "../components/cards/Visitors";
import Banks from "../components/cards/Banks";
import { getActiveBranchId, isErpApiSuccess, postDashboard } from "../erpClient";
import { createErpSocket } from "../utils/socketIo";
import {
  ERP_DASHBOARD_POOL_INVALIDATE_EVENT,
  isSocketForCurrentBranch,
} from "../utils/socketPoolEvents";
import {
  getCurrentPinType,
  getQuoteInvoiceListScope,
  QUOTE_INVOICE_LIST_SCOPE_EVENT,
} from "./const";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import { getTenantBrandImageSrcWithFallback } from "../erpClient";

let dashboardFetchPromise = null;
let dashboardCache = null;
let dashboardCacheTime = 0;
let dashboardCacheKey = "";
const DASHBOARD_DEDUPE_MS = 2000;

/** Quote mode + navbar "All" → type mixed; otherwise keep type all. */
function resolveDashboardRequestType() {
  const pin = String(getCurrentPinType() || "").toLowerCase();
  if (pin === "quote" && getQuoteInvoiceListScope() === "all") {
    return "mixed";
  }
  return "all";
}

function invalidateDashboardCache() {
  dashboardCache = null;
  dashboardCacheTime = 0;
  dashboardFetchPromise = null;
  dashboardCacheKey = "";
}

async function requestDashboard({ force = false } = {}) {
  const now = Date.now();
  const listType = resolveDashboardRequestType();
  const branchId = getActiveBranchId() || "default";
  const cacheKey = `${branchId}:${String(getCurrentPinType() || "access")}:${listType}`;

  if (!force) {
    if (dashboardFetchPromise && dashboardCacheKey === cacheKey) {
      return dashboardFetchPromise;
    }
    if (
      dashboardCache &&
      dashboardCacheKey === cacheKey &&
      now - dashboardCacheTime < DASHBOARD_DEDUPE_MS
    ) {
      return dashboardCache;
    }
  } else {
    invalidateDashboardCache();
  }

  dashboardCacheKey = cacheKey;
  dashboardFetchPromise = postDashboard({ type: listType })
    .then((res) => {
      if (isErpApiSuccess(res)) {
        dashboardCache = res;
        dashboardCacheTime = Date.now();
        dashboardCacheKey = cacheKey;
      }
      return res;
    })
    .finally(() => {
      dashboardFetchPromise = null;
    });

  return dashboardFetchPromise;
}

function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [hasDashboardPermission, setHasDashboardPermission] = useState(
    () => hasPermission(PERMISSIONS.DASHBOARD_VIEW)
  );

  // Listen for permission changes in real-time
  useEffect(() => {
    const handlePermissionsUpdated = () => {
      setHasDashboardPermission(hasPermission(PERMISSIONS.DASHBOARD_VIEW));
    };

    window.addEventListener("permissionsUpdated", handlePermissionsUpdated);
    window.addEventListener("erpAuthUpdated", handlePermissionsUpdated);

    return () => {
      window.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
      window.removeEventListener("erpAuthUpdated", handlePermissionsUpdated);
    };
  }, []);

  const fetchDashboard = useCallback(async ({ force = false } = {}) => {
    try {
      const res = await requestDashboard({ force });
      if (isErpApiSuccess(res)) {
        setDashboardData(res?.data || null);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    }
  }, []);

  useEffect(() => {
    if (hasDashboardPermission) {
      fetchDashboard();
    }
  }, [fetchDashboard, hasDashboardPermission]);

  useEffect(() => {
    if (!hasDashboardPermission) return;

    const handleApiUrlChange = () => {
      fetchDashboard({ force: true });
    };
    const handleQuoteScopeChange = () => {
      fetchDashboard({ force: true });
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    window.addEventListener(
      ERP_DASHBOARD_POOL_INVALIDATE_EVENT,
      handleApiUrlChange,
    );
    window.addEventListener(
      QUOTE_INVOICE_LIST_SCOPE_EVENT,
      handleQuoteScopeChange,
    );
    window.addEventListener("branchChanged", handleApiUrlChange);
    window.addEventListener("branchUpdated", handleApiUrlChange);
    window.addEventListener("permissionsUpdated", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
      window.removeEventListener(
        ERP_DASHBOARD_POOL_INVALIDATE_EVENT,
        handleApiUrlChange,
      );
      window.removeEventListener(
        QUOTE_INVOICE_LIST_SCOPE_EVENT,
        handleQuoteScopeChange,
      );
      window.removeEventListener("branchChanged", handleApiUrlChange);
      window.removeEventListener("branchUpdated", handleApiUrlChange);
      window.removeEventListener("permissionsUpdated", handleApiUrlChange);
    };
  }, [fetchDashboard, hasDashboardPermission]);

  useEffect(() => {
    if (!hasDashboardPermission) return;

    const socket = createErpSocket();

    const refreshPool = (payload) => {
      if (!isSocketForCurrentBranch(payload)) return;
      fetchDashboard({ force: true });
    };

    socket.on("metal_transfer_created", refreshPool);
    socket.on("tag_cancelled", refreshPool);

    return () => {
      socket.off("metal_transfer_created", refreshPool);
      socket.off("tag_cancelled", refreshPool);
      socket.disconnect();
    };
  }, [fetchDashboard, hasDashboardPermission]);

  // If user does NOT have dashboard permission, show the tenant logo as a landing page
  if (!hasDashboardPermission) {
    const logoUrl = getTenantBrandImageSrcWithFallback("logo");

    return (
      <div className="body-wrapper">
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "calc(100vh - 140px)" }}
        >
          <div className="text-center">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Company Logo"
                style={{
                  maxWidth: "280px",
                  maxHeight: "280px",
                  objectFit: "contain",
                  opacity: 0.85,
                }}
              />
            ) : (
              <div
                style={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  opacity: 0.7,
                }}
              >
                <i
                  className="ti ti-building-store"
                  style={{ fontSize: "3rem", color: "#fff" }}
                ></i>
              </div>
            )}
            <p
              className="text-muted mt-3"
              style={{ fontSize: "0.95rem" }}
            >
              Welcome! Use the sidebar to navigate.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Full dashboard for users with DASHBOARD_VIEW permission
  return (
    <div className="body-wrapper">
      <div className="container-fluid py-4">
        <div className="row g-4 align-items-start mt-1">
          <AvailableStock className="mt-0" stockData={dashboardData?.stock} />
          <YearlySales className="mt-0" salesData={dashboardData?.sales} />
          <Revenue className="mt-0" revenueData={dashboardData?.revenue} />
        </div>

        <div className="row g-4" style={{ marginTop: "-290px" }}>
          <Orders className="mt-0" ordersData={dashboardData?.orders} />
          <Visitors className="mt-0" visitorsData={dashboardData?.visitors} />
        </div>

        <div className="row g-4 mt-0 align-items-start">
          <Customers className="mt-0" customersData={dashboardData?.customers} />
          <DuePayable
            className="mt-0"
            duePayableData={dashboardData?.due_and_payable}
          />
          <Banks
            className="mt-0"
            bankBalancesData={dashboardData?.bank_balances}
          />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
